import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  DollarSign,
  Plus,
  RefreshCw,
  Settings,
  Eye,
  Play,
  FileText
} from 'lucide-react';

const RECEIVER_TYPES = [
  { value: 'COST_CENTER', label: 'Cost Center' },
  { value: 'GL_ACCOUNT', label: 'G/L Account' },
  { value: 'INTERNAL_ORDER', label: 'Internal Order' },
  { value: 'FIXED_ASSET', label: 'Fixed Asset' },
  { value: 'PROFITABILITY_SEGMENT', label: 'Profitability Segment' },
  { value: 'WBS_ELEMENT', label: 'WBS Element' }
];

export default function Settlement() {
  const [rules, setRules] = useState([]);
  const { canWrite } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [wbsElements, setWbsElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [settlementResult, setSettlementResult] = useState(null);
  const [activeTab, setActiveTab] = useState('rules');
  const [filterProject, setFilterProject] = useState('__all__');

  const [ruleForm, setRuleForm] = useState({
    project_id: '',
    wbs_element_id: '',
    settlement_percent: 100,
    receiver_type: 'COST_CENTER',
    receiver_id: '',
    valid_from: new Date().toISOString().split('T')[0],
    settle_costs: true,
    settle_revenues: false,
    description: ''
  });

  const [executeForm, setExecuteForm] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    period: new Date().getMonth() + 1,
    posting_date: new Date().toISOString().split('T')[0],
    settlement_type: 'PERIODIC',
    test_run: true
  });


  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterProject !== '__all__' ? { project_id: filterProject } : {};
      const [rulesRes, docsRes, projectsRes] = await Promise.all([
        psAPI.getSettlementRules(params),
        psAPI.getSettlements(params),
        psAPI.getProjects()
      ]);
      setRules(rulesRes.data);
      setDocuments(docsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterProject]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProjectChange = async (projectId) => {
    setRuleForm({ ...ruleForm, project_id: projectId, wbs_element_id: '' });
    if (projectId) {
      try {
        const response = await psAPI.getWBSElements({ project_id: projectId });
        setWbsElements(response.data);
      } catch (error) {
        setWbsElements([]);
      }
    }
  };

  const handleCreateRule = async () => {
    if (!ruleForm.project_id || !ruleForm.receiver_id) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await psAPI.createSettlementRule(ruleForm);
      toast.success('Settlement rule created');
      setShowRuleDialog(false);
      resetRuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    }
  };

  const handleExecuteSettlement = async () => {
    if (!executeForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    try {
      const response = await psAPI.executeSettlement(executeForm);
      setSettlementResult(response.data);
      setShowExecuteDialog(false);
      setShowResultDialog(true);
      if (!executeForm.test_run) {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute settlement');
    }
  };

  const resetRuleForm = () => {
    setRuleForm({
      project_id: '',
      wbs_element_id: '',
      settlement_percent: 100,
      receiver_type: 'COST_CENTER',
      receiver_id: '',
      valid_from: new Date().toISOString().split('T')[0],
      settle_costs: true,
      settle_revenues: false,
      description: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: 'bg-amber-100 text-amber-700',
      POSTED: 'bg-emerald-100 text-emerald-700',
      REVERSED: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settlement</h1>
            <p className="text-gray-500">Manage settlement rules and execute project settlements</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowExecuteDialog(true)} data-testid="execute-settlement-btn">
              <Play className="w-4 h-4 mr-2" />
              Execute Settlement
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex gap-4">
            <button
              className={`pb-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'rules' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500'}`}
              onClick={() => setActiveTab('rules')}
            >
              Settlement Rules
            </button>
            <button
              className={`pb-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'documents' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500'}`}
              onClick={() => setActiveTab('documents')}
            >
              Settlement Documents
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-4 items-end">
          <div className="w-64">
            <Label>Filter by Project</Label>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger>
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {activeTab === 'rules' && canWrite('project_system') && (
            <Button onClick={() => setShowRuleDialog(true)} data-testid="create-rule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Rule
            </Button>
          )}
        </div>

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-600" />
                Settlement Rules ({rules.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
                </div>
              ) : rules.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Project</th>
                        <th className="text-left p-3">WBS Element</th>
                        <th className="text-left p-3">Receiver Type</th>
                        <th className="text-left p-3">Receiver</th>
                        <th className="text-right p-3">%</th>
                        <th className="text-left p-3">Valid From</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((rule) => (
                        <tr key={rule.rule_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{rule.project_name || rule.project_id}</td>
                          <td className="p-3">{rule.wbs_element_name || rule.wbs_element_id || 'All'}</td>
                          <td className="p-3">{rule.receiver_type}</td>
                          <td className="p-3">{rule.receiver_name || rule.receiver_id}</td>
                          <td className="p-3 text-right">{rule.settlement_percent}%</td>
                          <td className="p-3">{rule.valid_from}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No settlement rules found</p>
                  <p className="text-sm">Create rules to define how project costs are settled</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                Settlement Documents ({documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
                </div>
              ) : documents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Document</th>
                        <th className="text-left p-3">Project</th>
                        <th className="text-left p-3">Period</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-right p-3">Costs Settled</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Posted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.document_id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-orange-600">{doc.document_number}</td>
                          <td className="p-3">{doc.project_name || doc.project_id}</td>
                          <td className="p-3">{doc.fiscal_year}/{doc.period}</td>
                          <td className="p-3">{doc.settlement_type}</td>
                          <td className="p-3 text-right font-medium">${(doc.total_costs_settled || 0).toLocaleString()}</td>
                          <td className="p-3">{getStatusBadge(doc.status)}</td>
                          <td className="p-3 text-sm">{doc.posted_at ? new Date(doc.posted_at).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No settlement documents found</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Settlement Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select value={ruleForm.project_id} onValueChange={handleProjectChange}>
                <SelectTrigger data-testid="rule-project">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>WBS Element (optional - leave blank for all)</Label>
              <Select 
                value={ruleForm.wbs_element_id || '__all__'} 
                onValueChange={(v) => setRuleForm({ ...ruleForm, wbs_element_id: v === '__all__' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All WBS Elements" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All WBS Elements</SelectItem>
                  {wbsElements.map(w => (
                    <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Receiver Type *</Label>
                <Select value={ruleForm.receiver_type} onValueChange={(v) => setRuleForm({ ...ruleForm, receiver_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECEIVER_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Settlement %</Label>
                <Input
                  type="number"
                  value={ruleForm.settlement_percent}
                  onChange={(e) => setRuleForm({ ...ruleForm, settlement_percent: parseFloat(e.target.value) })}
                  min="0"
                  max="100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Receiver ID *</Label>
              <Input
                value={ruleForm.receiver_id}
                onChange={(e) => setRuleForm({ ...ruleForm, receiver_id: e.target.value })}
                placeholder="e.g., CC-1000, 400000, IO-001"
              />
            </div>

            <div className="space-y-2">
              <Label>Valid From</Label>
              <Input
                type="date"
                value={ruleForm.valid_from}
                onChange={(e) => setRuleForm({ ...ruleForm, valid_from: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRule} data-testid="submit-rule">Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execute Settlement Dialog */}
      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Execute Settlement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select value={executeForm.project_id} onValueChange={(v) => setExecuteForm({ ...executeForm, project_id: v })}>
                <SelectTrigger data-testid="exec-project">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fiscal Year</Label>
                <Input
                  type="number"
                  value={executeForm.fiscal_year}
                  onChange={(e) => setExecuteForm({ ...executeForm, fiscal_year: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Period</Label>
                <Input
                  type="number"
                  value={executeForm.period}
                  onChange={(e) => setExecuteForm({ ...executeForm, period: parseInt(e.target.value) })}
                  min="1"
                  max="16"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Posting Date</Label>
              <Input
                type="date"
                value={executeForm.posting_date}
                onChange={(e) => setExecuteForm({ ...executeForm, posting_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Settlement Type</Label>
              <Select value={executeForm.settlement_type} onValueChange={(v) => setExecuteForm({ ...executeForm, settlement_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERIODIC">Periodic</SelectItem>
                  <SelectItem value="FINAL">Final</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
              <input
                type="checkbox"
                checked={executeForm.test_run}
                onChange={(e) => setExecuteForm({ ...executeForm, test_run: e.target.checked })}
                id="test-run"
              />
              <label htmlFor="test-run" className="text-sm text-amber-700">
                Test Run (simulate without posting)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExecuteDialog(false)}>Cancel</Button>
            <Button onClick={handleExecuteSettlement} data-testid="submit-settlement">
              {executeForm.test_run ? 'Run Test' : 'Post Settlement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settlement Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Settlement Result</DialogTitle>
          </DialogHeader>
          {settlementResult && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gray-50 rounded-lg grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Document</Label>
                  <p className="font-medium">{settlementResult.document_number}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <p>{getStatusBadge(settlementResult.status)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Total Settled</Label>
                  <p className="font-medium text-emerald-600">${(settlementResult.total_costs_settled || 0).toLocaleString()}</p>
                </div>
              </div>

              {settlementResult.line_items?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">WBS Element</th>
                        <th className="text-left p-2">Receiver</th>
                        <th className="text-right p-2">Costs</th>
                        <th className="text-right p-2">Settled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlementResult.line_items.map((item, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-2">{item.wbs_element_name || item.wbs_element_id}</td>
                          <td className="p-2">{item.receiver_type}: {item.receiver_id}</td>
                          <td className="p-2 text-right">${(item.costs_to_settle || 0).toLocaleString()}</td>
                          <td className="p-2 text-right font-medium">${(item.settled_costs || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowResultDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
